import { useCallback, useEffect } from 'react';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { Toast, showErrorToUser, useInput } from '@pletly/ui';

import {
	CurrentTermsOfUseDocument,
	useCurrentTermsOfUseQuery,
} from './__generated__/currentTerms.generated';
import { useNewTermsOfUseMutation } from './__generated__/newTermsOfUse.generated';
import { GetAllTermsOfUseDocument } from '../../components/__generated__/allTerms.generated';

export const useCreateTerms = ({ organisationUuid }: { organisationUuid: string }) => {
	const { t } = useTranslation();
	const { navigate } = useNavigation();
	const { data, loading: currentTermsLoading } = useCurrentTermsOfUseQuery({
		variables: {
			organisationUuid,
		},
	});
	const termsInput = useInput(
		data?.organisation.termsOfUse?.text || data?.organisation.termsOfUseTemplate || '',
	);
	const { reset } = termsInput;
	const [createTermsOfUse, { loading }] = useNewTermsOfUseMutation({
		variables: {
			organisationUuid,
			text: termsInput.value,
		},
		refetchQueries: [GetAllTermsOfUseDocument, CurrentTermsOfUseDocument],
	});

	useEffect(() => reset(), [data, reset]);

	const handleSave = useCallback(async () => {
		if (loading) return;

		try {
			await createTermsOfUse();

			Toast.show({
				type: 'success',
				text1: t('terms-of-use.terms-of-use-created'),
			});

			return navigate('AllTermsOfUse', { uuid: organisationUuid });
		} catch (error) {
			showErrorToUser(error as Error);
		}
	}, [t, createTermsOfUse, loading, navigate, organisationUuid]);

	const canSave = !!termsInput.value && !loading;

	return { currentTermsLoading, canSave, handleSave, termsInput };
};
